/**
 * The Transit sidebar's transitions list. Each transition folds out into its
 * nodes with a status icon (StatusIcon), like the prototype's sidebar and RecentProgramsNav.
 * Readiness, the Map and Generate package follow the nodes (not sections, but views of the same
 * transition). Nodes load the first time a row opens; after that, the transition you're in
 * takes each graph the workspace loads (TRANSIT_GRAPH), so its icons move with the page. The transition you're in opens when you go to it, and collapses
 * like any other. The sample transition (./sample) is listed first until the
 * user creates a transition of their own, and again once they replay the tour.
 */
import { useEffect, useState } from 'react'
import { NavLink, useMatch, useSearchParams } from 'react-router-dom'
import { ChevronRight, FileText, Gauge, Loader2, Network } from 'lucide-react'
import { transitApi } from '../../services/transit'
import type { GraphNode, Transition } from '../../types/transit'
import StatusIcon from './StatusIcon'
import { nodeStatusLabel, TRANSIT_CHANGED, TRANSIT_GRAPH, type TransitGraphEvent } from './status'
import { inDisplayOrder, MAP, PACKAGE, READINESS } from './displayOrder'
import { useTransitSession } from './session'
import { SAMPLE_TRANSITION, SAMPLE_TRANSITION_ID } from './sample/data'
import { TRANSIT_TOUR } from './TransitTour'

function TransitionRow({ transition }: { transition: Transition }) {
  const match = useMatch('/transit/:transitionId')
  const [params] = useSearchParams()
  const current = match?.params.transitionId === transition.id
  const [open, setOpen] = useState(current)
  // Open on arriving at this transition; the chevron can still collapse it.
  useEffect(() => { if (current) setOpen(true) }, [current])
  const [nodes, setNodes] = useState<GraphNode[] | null>(null)
  const expanded = open

  useEffect(() => {
    if (!expanded) return
    let cancelled = false
    transitApi.getGraph(transition.id)
      .then((g) => { if (!cancelled) setNodes(inDisplayOrder(g.nodes)) })
      .catch(() => { if (!cancelled) setNodes([]) })
    return () => { cancelled = true }
  }, [expanded, transition.id])

  // The workspace's own loads of this transition, as they happen.
  useEffect(() => {
    const onGraph = (e: Event) => {
      const { transitionId, nodes: fresh } = (e as CustomEvent<TransitGraphEvent>).detail
      if (transitionId === transition.id) setNodes(inDisplayOrder(fresh))
    }
    window.addEventListener(TRANSIT_GRAPH, onGraph)
    return () => window.removeEventListener(TRANSIT_GRAPH, onGraph)
  }, [transition.id])

  const activeNode = current ? params.get('node') : null

  return (
    <div data-tour={transition.id === SAMPLE_TRANSITION_ID ? 'sample' : undefined}>
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={expanded ? 'Collapse nodes' : 'Expand nodes'}
          aria-expanded={expanded}
          className="flex shrink-0 items-center justify-center px-1.5 text-muted-foreground hover:text-foreground"
        >
          <ChevronRight className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
        <NavLink
          to={`/transit/${transition.id}`}
          // The name folds the sections like the chevron. It also opens the
          // transition, except when you're already in it: folding mustn't
          // move you off the section you're on.
          onClick={(e) => {
            if (current) e.preventDefault()
            setOpen((v) => !v)
          }}
          // Bold whenever you're in this transition, on any of its sections.
          className={`min-w-0 flex-1 truncate rounded-md px-2 py-3 text-sm leading-[18px] transition-colors ${current ? 'font-semibold' : ''} ${
            current && !activeNode ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'text-sidebar-foreground hover:bg-sidebar-accent/60'
          }`}
        >
          {transition.name}
        </NavLink>
      </div>
      {expanded && (
        <div className="mb-1 ml-3 space-y-0.5 border-l border-sidebar-border pl-2">
          {nodes === null && (
            <div className="flex items-center gap-2 px-2 py-1.5 text-[11px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading…
            </div>
          )}
          {nodes?.map((n) => (
            <NavLink
              key={n.node_key}
              to={`/transit/${transition.id}?node=${n.node_key}`}
              title={nodeStatusLabel(n.status)}
              className={`flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors ${
                activeNode === n.node_key ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'text-sidebar-foreground/90 hover:bg-sidebar-accent/60'
              }`}
            >
              <StatusIcon status={n.status} />
              <span className="truncate">{n.name}</span>
            </NavLink>
          ))}
          {nodes !== null && [
            { key: READINESS, label: 'Readiness', Icon: Gauge },
            { key: MAP, label: 'Map', Icon: Network },
            { key: PACKAGE, label: 'Generate package', Icon: FileText },
          ].map(({ key, label, Icon }) => (
            <NavLink
              key={key}
              to={`/transit/${transition.id}?node=${key}`}
              className={`flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors ${
                activeNode === key ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'text-sidebar-foreground/90 hover:bg-sidebar-accent/60'
              }`}
            >
              <Icon className="h-3.5 w-3.5 shrink-0 -ml-[3px] -mr-[3px]" aria-hidden />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}

export default function TransitSidebarNav() {
  const { user } = useTransitSession()
  const [transitions, setTransitions] = useState<Transition[]>([])
  // The tour points at the sample's row, so a replay lists it.
  const [touring, setTouring] = useState(false)
  useEffect(() => {
    const onTour = () => setTouring(true)
    window.addEventListener(TRANSIT_TOUR, onTour)
    return () => window.removeEventListener(TRANSIT_TOUR, onTour)
  }, [])

  useEffect(() => {
    const load = () => {
      transitApi.listTransitions().then(setTransitions).catch(() => setTransitions([]))
    }
    load()
    window.addEventListener(TRANSIT_CHANGED, load)
    return () => window.removeEventListener(TRANSIT_CHANGED, load)
  }, [])

  const shown = !touring && transitions.some((t) => t.created_by === user.id) ? transitions : [SAMPLE_TRANSITION, ...transitions]

  return (
    <div className="flex flex-col gap-2">
      <div className="px-3 text-sm text-sidebar-foreground">Transitions</div>
      {shown.length === 0 ? (
        <div className="px-3 text-sm text-muted-foreground">None yet</div>
      ) : (
        <div className="space-y-0.5">
          {shown.slice(0, 15).map((t) => <TransitionRow key={t.id} transition={t} />)}
        </div>
      )}
    </div>
  )
}
